import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Globe,
  Award,
  Users,
  Target,
  CheckCircle,
  ArrowRight,
  Menu,
  X,
} from "lucide-react";
import { useLanguage } from "../../utils/LanguageContext";
import content from "../../content/about.json"

const AboutPage = () => {

  const { language, setLanguage } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const t = content[language];

  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  // Enhanced 3D Sound Wave Logo
  const SoundWaveLogo = () => {
    const uniqueId = Math.random().toString(36).substr(2, 9);

    return (
      <div className="w-10 h-10 bg-gradient-to-r from-[#15B77C] to-[#1AA06A] rounded-lg flex items-center justify-center relative overflow-hidden shadow-lg">
        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none">
          <rect x="2" y="11" width="2" height="2" rx="1" fill="currentColor" />
          <rect x="5.5" y="9" width="2" height="6" rx="1" fill="currentColor" />
          <rect x="9" y="7" width="2" height="10" rx="1" fill="currentColor" />
          <rect
            x="12.5"
            y="4"
            width="2"
            height="16"
            rx="1"
            fill="currentColor"
          />
          <rect x="16" y="8" width="2" height="8" rx="1" fill="currentColor" />
          <rect
            x="19.5"
            y="10"
            width="2"
            height="4"
            rx="1"
            fill="currentColor"
          />
        </svg>
      </div>
    );
  };

  // Language Toggle Component
  const LanguageToggle = () => (
    <div className="flex items-center space-x-2 bg-[#011f4b] rounded-full p-1 sm:w-[100px] w-[100px]">
      <button
        onClick={() => setLanguage("es")}
        className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
          language === "es"
            ? "bg-lexora-accent text-white shadow-lg"
            : "text-white hover:text-white"
        }`}
      >
        ES
      </button>
      <button
        onClick={() => setLanguage("en")}
        className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
          language === "en"
            ? "bg-lexora-accent text-white shadow-lg"
            : "text-white hover:text-white"
        }`}
      >
        EN
      </button>
    </div>
  );



  return (
    <div className="min-h-screen bg-lexora-dark">
      {/* <Header /> */}

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-[#0D2642] overflow-hidden">
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-20 left-10 w-32 h-32 bg-lexora-accent/20 rounded-full blur-3xl"
            animate={{ y: [0, -20, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
          />
          <motion.div
            className="absolute bottom-20 right-10 w-48 h-48 bg-white/10 rounded-full blur-3xl"
            animate={{ y: [0, 20, 0] }}
            transition={{ duration: 5, repeat: Infinity }}
          />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:pt-20 md:pt-20 lg:pt-20 pt-20 pb-0 sm:pb-0 sm:pb-0 lg:pb-0 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="leading-tight xl:leading-tight text-4xl sm:text-5xl md:text-5xl lg:text-5xl font-heading font-bold text-white leading-heading tracking-heading mb-6"
          >
            <span className="block">{t.hero.title}</span>
            <span className="block gradient-text">{t.hero.subtitle}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-white mb-8 leading-relaxed max-w-3xl mx-auto"
          >
            {t.hero.description}
          </motion.p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-12 sm:pt-12 md:pt-12 lg:pt-16 xl:py-16 2xl:py-24 pb-0 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center "
          >
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-[#333] tracking-heading leading-heading mb-8">
              {t.mission.title}
            </h2>
            <p className="text-xl text-[#333] max-w-3xl mx-auto font-body leading-body">
              {t.mission.text}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-10 sm:py-12 md:py-12 lg:py-16 xl:py-16 2xl:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {t.values.map((value, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-[#0D2642] rounded-2xl p-8 text-center card-hover"
              >
                <div className=" text-lexora-accent mb-6 flex justify-center">
                  <div className="bg-[#15b77c4d]  inline-flex justify-center items-center rounded-full w-16 h-16">
                    {index === 0 && <Award className="w-10 h-10" />}
                    {index === 1 && <Target className="w-10 h-10" />}
                    {index === 2 && <CheckCircle className="w-10 h-10" />}
                    {index === 3 && <Globe className="w-10 h-10" />}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">
                  {value.title}
                </h3>
                <p className="text-white">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-10 sm:py-12 md:py-12 lg:py-16 xl:py-16 2xl:py-24 bg-[#0D2642]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-0 sm:mb-0 md:mb-0 lg:mb-10 xl:mb-16 2xl:mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-10 sm:mb-10 md:mb-10 lg:mb-12 xl:mb-16 2xl:mb-16">
              {t.stats.title}
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {t.stats.items.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl p-10 text-center"
                >
                  <div className="text-4xl font-bold gradient-text mb-2">
                    {stat.number}
                  </div>
                  <div className="text-[#333]">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-0 sm:py-5 md:py-5 lg:py-5 xl:py-6 2xl:py-6">
        <div className="py-10 2xl:py-14 xl:py-14 max-w-7xl mx-auto px-4 sm:px-4 md:px-4 lg:px-8 text-center">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <SoundWaveLogo />
            <span className="text-2xl font-heading font-bold text-gray-800 tracking-heading">
              Lexora
            </span>
          </div>
          <p className="text-gray-800 mb-8">
            {language === "es"
              ? "Transformando carreras a través de habilidades excepcionales de comunicación en inglés."
              : "Transforming careers through exceptional English communication skills."}
          </p>
          <div className="flex justify-center space-x-3 sm:space-x-4 md:space-x-4 lg:space-x-6 xl:space-x-6 2xl:space-x-6">
            <a
              href="/"
              className="text-gray-800 hover:text-[#1AA06A] font-semibold transition-colors whitespace-nowrap font-body"
            >
              Home
            </a>
            <a
              href="/method"
              className="text-gray-800 hover:text-[#1AA06A] font-semibold transition-colors whitespace-nowrap font-body"
            >
              {t.nav.method}
            </a>
            <a
              href="/instructors"
              className="text-gray-800 hover:text-[#1AA06A] font-semibold transition-colors whitespace-nowrap font-body"
            >
              {t.nav.instructors}
            </a>
            <a
              href="/success"
              className="text-gray-800 hover:text-[#1AA06A] font-semibold transition-colors whitespace-nowrap font-body"
            >
              {t.nav.success}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
